import React, { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  TrophyIcon,
  StarIcon,
  FireIcon,
  ChartBarIcon,
  UserGroupIcon,
  AcademicCapIcon,
  SparklesIcon,
  ClockIcon,
  BoltIcon,
  GiftIcon,
  ChevronDownIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
  BookOpenIcon,
  DocumentTextIcon,
  UsersIcon
} from '@heroicons/react/24/outline'
import {
  TrophyIcon as TrophySolidIcon,
  StarIcon as StarSolidIcon,
  FireIcon as FireSolidIcon,
  SparklesIcon as SparklesSolidIcon
} from '@heroicons/react/24/solid'
import {
  useGetAllAchievementsQuery,
  useGetUserProgressQuery,
  useGetGlobalLeaderboardQuery,
  useGetUserStatsQuery
} from '../store/api/gamificationApi'

// Achievement interface
interface Achievement {
  id: string
  title: string
  description: string
  type: 'study_time' | 'exam_score' | 'streak' | 'mindmap' | 'social'
  target: number
  current: number
  completed: boolean
  completedAt?: string
  reward: {
    xp: number
    coins: number
    badge?: string
  }
  rarity: 'common' | 'rare' | 'epic' | 'legendary'
  category: string
  icon: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

interface LeaderboardUser {
  id: string
  name: string
  avatar: string
  totalXP: number
  completedAchievements: number
  rank: number
  badge?: string
  level: number
}

interface RecentActivity {
  id: string
  userId: string
  userName: string
  userAvatar: string
  achievementId: string
  achievementTitle: string
  achievementRarity: 'common' | 'rare' | 'epic' | 'legendary'
  completedAt: string
  type: 'achievement_unlocked' | 'level_up' | 'streak_milestone'
}

// Category icons mapping
const categoryIcons = {
  study_time: ClockIcon,
  exam_score: TrophyIcon,
  streak: FireIcon,
  mindmap: ChartBarIcon,
  social: UserGroupIcon
}

// Rarity colors with premium gradients
const rarityColors = {
  common: {
    bg: 'bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 dark:from-slate-800 dark:via-gray-800 dark:to-slate-700',
    border: 'border-slate-200/60 dark:border-slate-600/60',
    text: 'text-slate-700 dark:text-slate-300',
    accent: 'bg-gradient-to-r from-slate-100 to-gray-100 dark:from-slate-700 dark:to-gray-700',
    glow: 'shadow-lg shadow-slate-200/30 dark:shadow-slate-700/30',
    shimmer: 'from-slate-200 via-white to-slate-200 dark:from-slate-700 dark:via-slate-600 dark:to-slate-700',
    ring: 'ring-slate-200/50 dark:ring-slate-600/50'
  },
  rare: {
    bg: 'bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-100 dark:from-blue-900 dark:via-cyan-900 dark:to-blue-800',
    border: 'border-blue-300/70 dark:border-blue-500/70',
    text: 'text-blue-700 dark:text-blue-300',
    accent: 'bg-gradient-to-r from-blue-100 to-cyan-100 dark:from-blue-700 dark:to-cyan-700',
    glow: 'shadow-xl shadow-blue-300/40 dark:shadow-blue-600/40',
    shimmer: 'from-blue-300 via-cyan-200 to-blue-300 dark:from-blue-600 dark:via-cyan-500 dark:to-blue-600',
    ring: 'ring-blue-300/60 dark:ring-blue-500/60'
  },
  epic: {
    bg: 'bg-gradient-to-br from-purple-50 via-fuchsia-50 to-pink-100 dark:from-purple-900 dark:via-fuchsia-900 dark:to-pink-800',
    border: 'border-purple-400/80 dark:border-purple-500/80',
    text: 'text-purple-700 dark:text-purple-300',
    accent: 'bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-700 dark:to-pink-700',
    glow: 'shadow-2xl shadow-purple-400/50 dark:shadow-purple-600/50',
    shimmer: 'from-purple-400 via-fuchsia-300 to-pink-400 dark:from-purple-600 dark:via-fuchsia-500 dark:to-pink-600',
    ring: 'ring-purple-400/70 dark:ring-purple-500/70'
  },
  legendary: {
    bg: 'bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-100 dark:from-amber-900 dark:via-yellow-900 dark:to-orange-800',
    border: 'border-amber-400/90 dark:border-amber-500/90',
    text: 'text-amber-700 dark:text-amber-300',
    accent: 'bg-gradient-to-r from-amber-100 to-orange-100 dark:from-amber-700 dark:to-orange-700',
    glow: 'shadow-2xl shadow-amber-400/60 dark:shadow-amber-600/60',
    shimmer: 'from-amber-400 via-yellow-300 to-orange-400 dark:from-amber-600 dark:via-yellow-500 dark:to-orange-600',
    ring: 'ring-amber-400/80 dark:ring-amber-500/80'
  }
}

// Mock achievements data
const mockAchievements: Achievement[] = [
  // Çalışma Kategorisi
  {
    id: 'study-master',
    title: 'Çalışma Ustası',
    description: '100 saat çalışma tamamla',
    type: 'study_time',
    target: 100,
    current: 75,
    completed: false,
    reward: { xp: 500, coins: 100, badge: 'study-master-badge' },
    rarity: 'epic',
    category: 'Çalışma',
    icon: '📚',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'early-bird',
    title: 'Erken Kuş',
    description: 'Sabah 6:00\'dan önce 10 kez çalış',
    type: 'study_time',
    target: 10,
    current: 3,
    completed: false,
    reward: { xp: 200, coins: 50, badge: 'early-bird-badge' },
    rarity: 'common',
    category: 'Çalışma',
    icon: '🌅',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'night-owl',
    title: 'Gece Baykuşu',
    description: 'Gece 22:00\'dan sonra 20 saat çalış',
    type: 'study_time',
    target: 20,
    current: 12,
    completed: false,
    reward: { xp: 300, coins: 75, badge: 'night-owl-badge' },
    rarity: 'rare',
    category: 'Çalışma',
    icon: '🦉',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'marathon-runner',
    title: 'Maraton Koşucusu',
    description: 'Tek seferde 8 saat çalış',
    type: 'study_time',
    target: 8,
    current: 5,
    completed: false,
    reward: { xp: 800, coins: 160, badge: 'marathon-badge' },
    rarity: 'legendary',
    category: 'Çalışma',
    icon: '🏃‍♂️',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'study-veteran',
    title: 'Çalışma Veteranı',
    description: '500 saat çalışma tamamla',
    type: 'study_time',
    target: 500,
    current: 245,
    completed: false,
    reward: { xp: 2000, coins: 400, badge: 'veteran-badge' },
    rarity: 'legendary',
    category: 'Çalışma',
    icon: '🎖️',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'focused-mind',
    title: 'Odaklanmış Zihin',
    description: '2 saat kesintisiz çalış',
    type: 'study_time',
    target: 2,
    current: 2,
    completed: true,
    completedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    reward: { xp: 150, coins: 30, badge: 'focus-badge' },
    rarity: 'common',
    category: 'Çalışma',
    icon: '🎯',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  
  // Sınav Kategorisi
  {
    id: 'exam-champion',
    title: 'Sınav Şampiyonu',
    description: '10 sınavda 90+ puan al',
    type: 'exam_score',
    target: 10,
    current: 10,
    completed: true,
    completedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    reward: { xp: 1000, coins: 200, badge: 'exam-champion-badge' },
    rarity: 'legendary',
    category: 'Sınav',
    icon: '🏆',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'perfect-score',
    title: 'Mükemmel Puan',
    description: 'Bir sınavda 100 puan al',
    type: 'exam_score',
    target: 1,
    current: 1,
    completed: true,
    completedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    reward: { xp: 500, coins: 100, badge: 'perfect-badge' },
    rarity: 'epic',
    category: 'Sınav',
    icon: '💯',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'quick-learner',
    title: 'Hızlı Öğrenci',
    description: '5 sınavı 10 dakikada bitir',
    type: 'exam_score',
    target: 5,
    current: 2,
    completed: false,
    reward: { xp: 300, coins: 60, badge: 'quick-badge' },
    rarity: 'rare',
    category: 'Sınav',
    icon: '⚡',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'exam-master',
    title: 'Sınav Ustası',
    description: '50 sınav tamamla',
    type: 'exam_score',
    target: 50,
    current: 32,
    completed: false,
    reward: { xp: 750, coins: 150, badge: 'exam-master-badge' },
    rarity: 'epic',
    category: 'Sınav',
    icon: '📝',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'comeback-king',
    title: 'Geri Dönüş Kralı',
    description: 'Başarısız sınavdan sonra 95+ puan al',
    type: 'exam_score',
    target: 1,
    current: 0,
    completed: false,
    reward: { xp: 600, coins: 120, badge: 'comeback-badge' },
    rarity: 'epic',
    category: 'Sınav',
    icon: '👑',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'first-try',
    title: 'İlk Denemede',
    description: 'İlk sınavında 80+ puan al',
    type: 'exam_score',
    target: 1,
    current: 1,
    completed: true,
    completedAt: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000).toISOString(),
    reward: { xp: 250, coins: 50, badge: 'first-try-badge' },
    rarity: 'rare',
    category: 'Sınav',
    icon: '🎯',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  
  // Seri Kategorisi
  {
    id: 'streak-warrior',
    title: 'Seri Savaşçısı',
    description: '30 gün üst üste çalış',
    type: 'streak',
    target: 30,
    current: 15,
    completed: false,
    reward: { xp: 750, coins: 150, badge: 'streak-warrior-badge' },
    rarity: 'rare',
    category: 'Seri',
    icon: '🔥',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'week-warrior',
    title: 'Hafta Savaşçısı',
    description: '7 gün üst üste çalış',
    type: 'streak',
    target: 7,
    current: 7,
    completed: true,
    completedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    reward: { xp: 200, coins: 40, badge: 'week-badge' },
    rarity: 'common',
    category: 'Seri',
    icon: '📅',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'streak-legend',
    title: 'Seri Efsanesi',
    description: '100 gün üst üste çalış',
    type: 'streak',
    target: 100,
    current: 45,
    completed: false,
    reward: { xp: 2500, coins: 500, badge: 'legend-badge' },
    rarity: 'legendary',
    category: 'Seri',
    icon: '🌟',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'weekend-warrior',
    title: 'Hafta Sonu Savaşçısı',
    description: '10 hafta sonu çalış',
    type: 'streak',
    target: 10,
    current: 6,
    completed: false,
    reward: { xp: 400, coins: 80, badge: 'weekend-badge' },
    rarity: 'rare',
    category: 'Seri',
    icon: '🎯',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'consistency-king',
    title: 'Tutarlılık Kralı',
    description: '3 gün üst üste çalış',
    type: 'streak',
    target: 3,
    current: 3,
    completed: true,
    completedAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
    reward: { xp: 100, coins: 20, badge: 'consistency-badge' },
    rarity: 'common',
    category: 'Seri',
    icon: '⭐',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  

  
  // Sosyal Kategorisi
  {
    id: 'social-butterfly',
    title: 'Sosyal Kelebek',
    description: '50 arkadaşla çalış',
    type: 'social',
    target: 50,
    current: 25,
    completed: false,
    reward: { xp: 300, coins: 60, badge: 'social-butterfly-badge' },
    rarity: 'common',
    category: 'Sosyal',
    icon: '👥',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'team-player',
    title: 'Takım Oyuncusu',
    description: '10 grup çalışması tamamla',
    type: 'social',
    target: 10,
    current: 4,
    completed: false,
    reward: { xp: 400, coins: 80, badge: 'team-badge' },
    rarity: 'rare',
    category: 'Sosyal',
    icon: '🤝',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'mentor',
    title: 'Mentor',
    description: '20 kişiye yardım et',
    type: 'social',
    target: 20,
    current: 8,
    completed: false,
    reward: { xp: 600, coins: 120, badge: 'mentor-badge' },
    rarity: 'epic',
    category: 'Sosyal',
    icon: '👨‍🏫',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'community-leader',
    title: 'Topluluk Lideri',
    description: '100 kişiyle etkileşim kur',
    type: 'social',
    target: 100,
    current: 35,
    completed: false,
    reward: { xp: 1000, coins: 200, badge: 'leader-badge' },
    rarity: 'legendary',
    category: 'Sosyal',
    icon: '👑',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'helpful-friend',
    title: 'Yardımsever Arkadaş',
    description: '5 kişiye yardım et',
    type: 'social',
    target: 5,
    current: 5,
    completed: true,
    completedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    reward: { xp: 150, coins: 30, badge: 'helpful-badge' },
    rarity: 'common',
    category: 'Sosyal',
    icon: '🤗',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  
  // Mind Map Kategorisi
  {
    id: 'mind-mapper',
    title: 'Zihin Haritacısı',
    description: '25 mind map oluştur',
    type: 'mindmap',
    target: 25,
    current: 12,
    completed: false,
    reward: { xp: 350, coins: 70, badge: 'mapper-badge' },
    rarity: 'common',
    category: 'Mind Map',
    icon: '🗺️',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'creative-thinker',
    title: 'Yaratıcı Düşünür',
    description: '100 node içeren mind map oluştur',
    type: 'mindmap',
    target: 1,
    current: 0,
    completed: false,
    reward: { xp: 500, coins: 100, badge: 'creative-badge' },
    rarity: 'epic',
    category: 'Mind Map',
    icon: '💡',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'visual-learner',
    title: 'Görsel Öğrenci',
    description: '50 mind map çalış',
    type: 'mindmap',
    target: 50,
    current: 28,
    completed: false,
    reward: { xp: 400, coins: 80, badge: 'visual-badge' },
    rarity: 'rare',
    category: 'Mind Map',
    icon: '👁️',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'first-map',
    title: 'İlk Harita',
    description: 'İlk mind map\'ini oluştur',
    type: 'mindmap',
    target: 1,
    current: 1,
    completed: true,
    completedAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    reward: { xp: 75, coins: 15, badge: 'first-map-badge' },
    rarity: 'common',
    category: 'Mind Map',
    icon: '🗺️',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  
  // Özel Başarımlar
  {
    id: 'first-steps',
    title: 'İlk Adımlar',
    description: 'İlk çalışma seansını tamamla',
    type: 'study_time',
    target: 1,
    current: 1,
    completed: true,
    completedAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    reward: { xp: 50, coins: 10, badge: 'first-badge' },
    rarity: 'common',
    category: 'Özel',
    icon: '👶',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'dedication',
    title: 'Adanmışlık',
    description: 'Uygulamayı 30 gün kullan',
    type: 'study_time',
    target: 30,
    current: 18,
    completed: false,
    reward: { xp: 500, coins: 100, badge: 'dedication-badge' },
    rarity: 'rare',
    category: 'Özel',
    icon: '💎',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'perfectionist',
    title: 'Mükemmeliyetçi',
    description: '10 sınavda hiç yanlış yapma',
    type: 'exam_score',
    target: 10,
    current: 3,
    completed: false,
    reward: { xp: 1200, coins: 240, badge: 'perfect-badge' },
    rarity: 'legendary',
    category: 'Özel',
    icon: '✨',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'explorer',
    title: 'Kaşif',
    description: 'Tüm özellikleri keşfet',
    type: 'study_time',
    target: 1,
    current: 0,
    completed: false,
    reward: { xp: 300, coins: 60, badge: 'explorer-badge' },
    rarity: 'rare',
    category: 'Özel',
    icon: '🧭',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'ultimate-scholar',
    title: 'Nihai Bilgin',
    description: 'Tüm başarımları tamamla',
    type: 'study_time',
    target: 1,
    current: 0,
    completed: false,
    reward: { xp: 5000, coins: 1000, badge: 'ultimate-badge' },
    rarity: 'legendary',
    category: 'Özel',
    icon: '🎓',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },

  // Ek Çalışma Başarımları
  {
    id: 'dawn-scholar',
    title: 'Şafak Bilgini',
    description: 'Sabah 5:00-7:00 arası 20 saat çalış',
    type: 'study_time',
    target: 20,
    current: 8,
    completed: false,
    reward: { xp: 400, coins: 80, badge: 'dawn-badge' },
    rarity: 'rare',
    category: 'Çalışma',
    icon: '🌄',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'midnight-scholar',
    title: 'Gece Yarısı Bilgini',
    description: 'Gece 00:00-02:00 arası 15 saat çalış',
    type: 'study_time',
    target: 15,
    current: 3,
    completed: false,
    reward: { xp: 350, coins: 70, badge: 'midnight-badge' },
    rarity: 'rare',
    category: 'Çalışma',
    icon: '🌙',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'power-hour',
    title: 'Güç Saati',
    description: '1 saatte 5 konu tamamla',
    type: 'study_time',
    target: 5,
    current: 2,
    completed: false,
    reward: { xp: 200, coins: 40, badge: 'power-badge' },
    rarity: 'common',
    category: 'Çalışma',
    icon: '⚡',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'study-machine',
    title: 'Çalışma Makinesi',
    description: 'Günde 12 saat çalış',
    type: 'study_time',
    target: 12,
    current: 7,
    completed: false,
    reward: { xp: 600, coins: 120, badge: 'machine-badge' },
    rarity: 'epic',
    category: 'Çalışma',
    icon: '🤖',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'weekend-grind',
    title: 'Hafta Sonu Çalışkanı',
    description: 'Hafta sonları toplam 50 saat çalış',
    type: 'study_time',
    target: 50,
    current: 22,
    completed: false,
    reward: { xp: 500, coins: 100, badge: 'weekend-grind-badge' },
    rarity: 'rare',
    category: 'Çalışma',
    icon: '📚',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },

  // Ek Sınav Başarımları
  {
    id: 'speed-demon',
    title: 'Hız Şeytanı',
    description: '5 dakikada sınav bitir',
    type: 'exam_score',
    target: 1,
    current: 0,
    completed: false,
    reward: { xp: 300, coins: 60, badge: 'speed-demon-badge' },
    rarity: 'rare',
    category: 'Sınav',
    icon: '💨',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'exam-marathon',
    title: 'Sınav Maratonu',
    description: 'Günde 10 sınav tamamla',
    type: 'exam_score',
    target: 10,
    current: 4,
    completed: false,
    reward: { xp: 400, coins: 80, badge: 'marathon-badge' },
    rarity: 'rare',
    category: 'Sınav',
    icon: '🏃‍♀️',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'accuracy-master',
    title: 'Doğruluk Ustası',
    description: '20 sınavda %95+ doğruluk',
    type: 'exam_score',
    target: 20,
    current: 8,
    completed: false,
    reward: { xp: 800, coins: 160, badge: 'accuracy-badge' },
    rarity: 'epic',
    category: 'Sınav',
    icon: '🎯',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'subject-master-math',
    title: 'Matematik Ustası',
    description: 'Matematik sınavlarında 25 kez 90+ puan',
    type: 'exam_score',
    target: 25,
    current: 12,
    completed: false,
    reward: { xp: 600, coins: 120, badge: 'math-master-badge' },
    rarity: 'epic',
    category: 'Sınav',
    icon: '🔢',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'subject-master-science',
    title: 'Fen Ustası',
    description: 'Fen sınavlarında 25 kez 90+ puan',
    type: 'exam_score',
    target: 25,
    current: 9,
    completed: false,
    reward: { xp: 600, coins: 120, badge: 'science-master-badge' },
    rarity: 'epic',
    category: 'Sınav',
    icon: '🧪',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'subject-master-turkish',
    title: 'Türkçe Ustası',
    description: 'Türkçe sınavlarında 25 kez 90+ puan',
    type: 'exam_score',
    target: 25,
    current: 15,
    completed: false,
    reward: { xp: 600, coins: 120, badge: 'turkish-master-badge' },
    rarity: 'epic',
    category: 'Sınav',
    icon: '📖',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'exam-perfectionist',
    title: 'Sınav Mükemmeliyetçisi',
    description: '5 sınavda arka arkaya 100 puan',
    type: 'exam_score',
    target: 5,
    current: 1,
    completed: false,
    reward: { xp: 1000, coins: 200, badge: 'perfectionist-badge' },
    rarity: 'legendary',
    category: 'Sınav',
    icon: '💎',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },

  // Ek Seri Başarımları
  {
    id: 'streak-rookie',
    title: 'Seri Çaylağı',
    description: '5 gün üst üste çalış',
    type: 'streak',
    target: 5,
    current: 5,
    completed: true,
    completedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    reward: { xp: 150, coins: 30, badge: 'rookie-badge' },
    rarity: 'common',
    category: 'Seri',
    icon: '🔥',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'month-warrior',
    title: 'Ay Savaşçısı',
    description: '30 gün üst üste çalış',
    type: 'streak',
    target: 30,
    current: 18,
    completed: false,
    reward: { xp: 750, coins: 150, badge: 'month-badge' },
    rarity: 'epic',
    category: 'Seri',
    icon: '🌙',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'streak-immortal',
    title: 'Seri Ölümsüzü',
    description: '365 gün üst üste çalış',
    type: 'streak',
    target: 365,
    current: 89,
    completed: false,
    reward: { xp: 5000, coins: 1000, badge: 'immortal-badge' },
    rarity: 'legendary',
    category: 'Seri',
    icon: '♾️',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'morning-streak',
    title: 'Sabah Serisi',
    description: '15 gün üst üste sabah çalış',
    type: 'streak',
    target: 15,
    current: 7,
    completed: false,
    reward: { xp: 300, coins: 60, badge: 'morning-streak-badge' },
    rarity: 'rare',
    category: 'Seri',
    icon: '☀️',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'evening-streak',
    title: 'Akşam Serisi',
    description: '15 gün üst üste akşam çalış',
    type: 'streak',
    target: 15,
    current: 9,
    completed: false,
    reward: { xp: 300, coins: 60, badge: 'evening-streak-badge' },
    rarity: 'rare',
    category: 'Seri',
    icon: '🌆',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },



  // Ek Sosyal Başarımları
  {
    id: 'social-butterfly',
    title: 'Sosyal Kelebek',
    description: '50 kişiyle etkileşim kur',
    type: 'social',
    target: 50,
    current: 28,
    completed: false,
    reward: { xp: 400, coins: 80, badge: 'butterfly-badge' },
    rarity: 'rare',
    category: 'Sosyal',
    icon: '🦋',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'study-buddy',
    title: 'Çalışma Arkadaşı',
    description: '25 grup çalışması tamamla',
    type: 'social',
    target: 25,
    current: 12,
    completed: false,
    reward: { xp: 500, coins: 100, badge: 'buddy-badge' },
    rarity: 'rare',
    category: 'Sosyal',
    icon: '👫',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'motivator',
    title: 'Motivatör',
    description: '50 kişiyi motive et',
    type: 'social',
    target: 50,
    current: 18,
    completed: false,
    reward: { xp: 600, coins: 120, badge: 'motivator-badge' },
    rarity: 'epic',
    category: 'Sosyal',
    icon: '💪',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'knowledge-sharer',
    title: 'Bilgi Paylaşımcısı',
    description: '100 not paylaş',
    type: 'social',
    target: 100,
    current: 34,
    completed: false,
    reward: { xp: 500, coins: 100, badge: 'sharer-badge' },
    rarity: 'rare',
    category: 'Sosyal',
    icon: '📤',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'social-legend',
    title: 'Sosyal Efsane',
    description: '500 kişiyle etkileşim kur',
    type: 'social',
    target: 500,
    current: 156,
    completed: false,
    reward: { xp: 2000, coins: 400, badge: 'social-legend-badge' },
    rarity: 'legendary',
    category: 'Sosyal',
    icon: '🌟',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },

  // Ek Mind Map Başarımları
  {
    id: 'map-rookie',
    title: 'Harita Çaylağı',
    description: '5 mind map oluştur',
    type: 'mindmap',
    target: 5,
    current: 5,
    completed: true,
    completedAt: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(),
    reward: { xp: 100, coins: 20, badge: 'map-rookie-badge' },
    rarity: 'common',
    category: 'Mind Map',
    icon: '🗺️',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'complex-mapper',
    title: 'Karmaşık Haritacı',
    description: '50+ node içeren 10 mind map oluştur',
    type: 'mindmap',
    target: 10,
    current: 3,
    completed: false,
    reward: { xp: 600, coins: 120, badge: 'complex-badge' },
    rarity: 'epic',
    category: 'Mind Map',
    icon: '🧩',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'map-master',
    title: 'Harita Ustası',
    description: '100 mind map oluştur',
    type: 'mindmap',
    target: 100,
    current: 34,
    completed: false,
    reward: { xp: 800, coins: 160, badge: 'map-master-badge' },
    rarity: 'epic',
    category: 'Mind Map',
    icon: '🎯',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'visual-genius',
    title: 'Görsel Dahi',
    description: '200+ node içeren mind map oluştur',
    type: 'mindmap',
    target: 1,
    current: 0,
    completed: false,
    reward: { xp: 1000, coins: 200, badge: 'genius-badge' },
    rarity: 'legendary',
    category: 'Mind Map',
    icon: '🧠',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'map-legend',
    title: 'Harita Efsanesi',
    description: '500 mind map oluştur',
    type: 'mindmap',
    target: 500,
    current: 78,
    completed: false,
    reward: { xp: 2500, coins: 500, badge: 'map-legend-badge' },
    rarity: 'legendary',
    category: 'Mind Map',
    icon: '🏆',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },

  // Ek Özel Başarımları
  {
    id: 'early-adopter',
    title: 'Erken Benimseyici',
    description: 'Beta sürümünü kullan',
    type: 'study_time',
    target: 1,
    current: 1,
    completed: true,
    completedAt: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
    reward: { xp: 200, coins: 40, badge: 'early-adopter-badge' },
    rarity: 'rare',
    category: 'Özel',
    icon: '🚀',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'feedback-hero',
    title: 'Geri Bildirim Kahramanı',
    description: '10 geri bildirim gönder',
    type: 'social',
    target: 10,
    current: 3,
    completed: false,
    reward: { xp: 300, coins: 60, badge: 'feedback-badge' },
    rarity: 'rare',
    category: 'Özel',
    icon: '💬',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'bug-hunter',
    title: 'Hata Avcısı',
    description: '5 hata raporu gönder',
    type: 'social',
    target: 5,
    current: 1,
    completed: false,
    reward: { xp: 400, coins: 80, badge: 'bug-hunter-badge' },
    rarity: 'epic',
    category: 'Özel',
    icon: '🐛',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'feature-requester',
    title: 'Özellik İsteyicisi',
    description: '3 özellik önerisi gönder',
    type: 'social',
    target: 3,
    current: 0,
    completed: false,
    reward: { xp: 250, coins: 50, badge: 'feature-badge' },
    rarity: 'rare',
    category: 'Özel',
    icon: '💡',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'loyal-user',
    title: 'Sadık Kullanıcı',
    description: 'Uygulamayı 100 gün kullan',
    type: 'study_time',
    target: 100,
    current: 45,
    completed: false,
    reward: { xp: 1000, coins: 200, badge: 'loyal-badge' },
    rarity: 'epic',
    category: 'Özel',
    icon: '💎',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'achievement-hunter',
    title: 'Başarım Avcısı',
    description: '50 başarım tamamla',
    type: 'study_time',
    target: 50,
    current: 12,
    completed: false,
    reward: { xp: 1500, coins: 300, badge: 'hunter-badge' },
    rarity: 'legendary',
    category: 'Özel',
    icon: '🏹',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'time-master',
    title: 'Zaman Ustası',
    description: 'Pomodoro tekniğini 100 kez kullan',
    type: 'study_time',
    target: 100,
    current: 23,
    completed: false,
    reward: { xp: 500, coins: 100, badge: 'time-master-badge' },
    rarity: 'rare',
    category: 'Özel',
    icon: '⏰',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'goal-setter',
    title: 'Hedef Belirleyici',
    description: '25 hedef belirle',
    type: 'study_time',
    target: 25,
    current: 8,
    completed: false,
    reward: { xp: 300, coins: 60, badge: 'goal-badge' },
    rarity: 'common',
    category: 'Özel',
    icon: '🎯',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'goal-achiever',
    title: 'Hedef Başarıcısı',
    description: '20 hedefi tamamla',
    type: 'study_time',
    target: 20,
    current: 6,
    completed: false,
    reward: { xp: 600, coins: 120, badge: 'achiever-badge' },
    rarity: 'epic',
    category: 'Özel',
    icon: '🏆',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'data-analyst',
    title: 'Veri Analisti',
    description: 'İstatistikleri 50 kez kontrol et',
    type: 'study_time',
    target: 50,
    current: 18,
    completed: false,
    reward: { xp: 250, coins: 50, badge: 'analyst-badge' },
    rarity: 'common',
    category: 'Özel',
    icon: '📊',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }
]

const mockLeaderboard: LeaderboardUser[] = [
  {
    id: '1',
    name: 'Ahmet Yılmaz',
    avatar: '👨‍🎓',
    totalXP: 15420,
    completedAchievements: 28,
    rank: 1,
    badge: 'legendary-scholar',
    level: 42
  },
  {
    id: '2',
    name: 'Zeynep Kaya',
    avatar: '👩‍💼',
    totalXP: 14850,
    completedAchievements: 25,
    rank: 2,
    badge: 'epic-achiever',
    level: 39
  },
  {
    id: '3',
    name: 'Mehmet Demir',
    avatar: '👨‍💻',
    totalXP: 13200,
    completedAchievements: 22,
    rank: 3,
    badge: 'study-master',
    level: 35
  },
  {
    id: '4',
    name: 'Ayşe Özkan',
    avatar: '👩‍🔬',
    totalXP: 12100,
    completedAchievements: 20,
    rank: 4,
    level: 32
  },
  {
    id: '5',
    name: 'Can Arslan',
    avatar: '👨‍🎨',
    totalXP: 11500,
    completedAchievements: 18,
    rank: 5,
    level: 30
  }
]

const mockRecentActivities: RecentActivity[] = [
  {
    id: '1',
    userId: '2',
    userName: 'Zeynep Kaya',
    userAvatar: '👩‍💼',
    achievementId: 'exam-champion',
    achievementTitle: 'Sınav Şampiyonu',
    achievementRarity: 'legendary',
    completedAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
    type: 'achievement_unlocked'
  },
  {
    id: '2',
    userId: '1',
    userName: 'Ahmet Yılmaz',
    userAvatar: '👨‍🎓',
    achievementId: 'streak-master',
    achievementTitle: 'Seri Ustası',
    achievementRarity: 'epic',
    completedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    type: 'achievement_unlocked'
  },
  {
    id: '3',
    userId: '3',
    userName: 'Mehmet Demir',
    userAvatar: '👨‍💻',
    achievementId: 'level-up',
    achievementTitle: 'Seviye 35',
    achievementRarity: 'rare',
    completedAt: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    type: 'level_up'
  },
  {
    id: '4',
    userId: '4',
    userName: 'Ayşe Özkan',
    userAvatar: '👩‍🔬',
    achievementId: 'study-expert',
    achievementTitle: 'Çalışma Uzmanı',
    achievementRarity: 'rare',
    completedAt: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    type: 'achievement_unlocked'
  },
  {
    id: '5',
    userId: '5',
    userName: 'Can Arslan',
    userAvatar: '👨‍🎨',
    achievementId: 'streak-milestone',
    achievementTitle: '15 Günlük Seri',
    achievementRarity: 'common',
    completedAt: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
    type: 'streak_milestone'
  }
]

const Achievements: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [selectedRarity, setSelectedRarity] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState<'progress' | 'rarity' | 'date'>('progress')
  const [showCompleted, setShowCompleted] = useState(true)
  const [selectedAchievement, setSelectedAchievement] = useState<Achievement | null>(null)

  // Mock user ID
  const userId = 'mock-user'

  // API queries
  const { data: userProgress } = useGetUserProgressQuery(userId)
  const { data: leaderboard } = useGetGlobalLeaderboardQuery({ limit: 10 })
  const { data: userStats } = useGetUserStatsQuery(userId)

  // Use mock data for now
  const achievements = mockAchievements

  // Filter and sort achievements
  const filteredAchievements = useMemo(() => {
    let filtered = achievements.filter(achievement => {
      const matchesCategory = selectedCategory === 'all' || achievement.type === selectedCategory
      const matchesRarity = selectedRarity === 'all' || achievement.rarity === selectedRarity
      const matchesSearch = achievement.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           achievement.description.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesCompleted = showCompleted || !achievement.completed
      
      return matchesCategory && matchesRarity && matchesSearch && matchesCompleted
    })

    // Sort achievements
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'progress':
          return (b.current / b.target) - (a.current / a.target)
        case 'rarity':
          const rarityOrder = { legendary: 4, epic: 3, rare: 2, common: 1 }
          return rarityOrder[b.rarity] - rarityOrder[a.rarity]
        case 'date':
          return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        default:
          return 0
      }
    })

    return filtered
  }, [achievements, selectedCategory, selectedRarity, searchTerm, sortBy, showCompleted])

  // Statistics
  const stats = useMemo(() => {
    const total = achievements.length
    const completed = achievements.filter(a => a.completed).length
    const inProgress = achievements.filter(a => !a.completed && a.current > 0).length
    const notStarted = achievements.filter(a => a.current === 0).length
    
    return { total, completed, inProgress, notStarted }
  }, [achievements])

  // Categories for filter
  const categories = [
    { id: 'all', name: 'Tümü', icon: SparklesIcon },
    { id: 'study_time', name: 'Çalışma', icon: ClockIcon },
    { id: 'exam_score', name: 'Sınav', icon: TrophyIcon },
    { id: 'streak', name: 'Seri', icon: FireIcon },

    { id: 'social', name: 'Sosyal', icon: UserGroupIcon }
  ]

  const rarities = [
    { id: 'all', name: 'Tüm Nadirlık' },
    { id: 'common', name: 'Yaygın' },
    { id: 'rare', name: 'Nadir' },
    { id: 'epic', name: 'Epik' },
    { id: 'legendary', name: 'Efsanevi' }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center space-x-4 mb-4">
          <div className="p-3 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl shadow-lg">
            <TrophySolidIcon className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
              Başarımlar
            </h1>
            <p className="text-gray-600 dark:text-gray-400 text-lg">
              Hedeflerinizi tamamlayın ve ödüller kazanın
            </p>
          </div>
        </div>

        {/* Enhanced Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Achievements */}
          <motion.div
            whileHover={{ scale: 1.02, y: -5 }}
            className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/50 dark:to-blue-800/50 rounded-2xl p-6 border border-blue-200/50 dark:border-blue-700/50 shadow-lg backdrop-blur-sm"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-500 rounded-xl shadow-lg">
                <TrophyIcon className="h-6 w-6 text-white" />
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                  {mockAchievements.filter(a => a.completed).length}
                </div>
                <div className="text-sm text-blue-600 dark:text-blue-400">/ {mockAchievements.length}</div>
              </div>
            </div>
            <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-2">Toplam Başarım</h3>
            <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2">
              <div 
                className="bg-blue-500 h-2 rounded-full transition-all duration-700"
                style={{ width: `${(mockAchievements.filter(a => a.completed).length / mockAchievements.length) * 100}%` }}
              />
            </div>
          </motion.div>

          {/* Completion Rate */}
          <motion.div
            whileHover={{ scale: 1.02, y: -5 }}
            className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/50 dark:to-green-800/50 rounded-2xl p-6 border border-green-200/50 dark:border-green-700/50 shadow-lg backdrop-blur-sm"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-500 rounded-xl shadow-lg">
                <CheckCircleIcon className="h-6 w-6 text-white" />
              </div>
              <div className="text-2xl font-bold text-green-700 dark:text-green-300">
                {Math.round((mockAchievements.filter(a => a.completed).length / mockAchievements.length) * 100)}%
              </div>
            </div>
            <h3 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-2">Tamamlanma Oranı</h3>
            <p className="text-sm text-green-600 dark:text-green-400">
              Harika ilerleme! 🎉
            </p>
          </motion.div>

          {/* Legendary Count */}
          <motion.div
            whileHover={{ scale: 1.02, y: -5 }}
            className="bg-gradient-to-br from-amber-50 to-orange-100 dark:from-amber-900/50 dark:to-orange-800/50 rounded-2xl p-6 border border-amber-200/50 dark:border-amber-700/50 shadow-lg backdrop-blur-sm"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl shadow-lg">
                <StarSolidIcon className="h-6 w-6 text-white" />
              </div>
              <div className="text-2xl font-bold text-amber-700 dark:text-amber-300">
                {mockAchievements.filter(a => a.completed && a.rarity === 'legendary').length}
              </div>
            </div>
            <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-200 mb-2">Efsanevi Başarımlar</h3>
            <p className="text-sm text-amber-600 dark:text-amber-400">
              Nadir başarımlar! 👑
            </p>
          </motion.div>

          {/* Recent Achievement */}
          <motion.div
            whileHover={{ scale: 1.02, y: -5 }}
            className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/50 dark:to-purple-800/50 rounded-2xl p-6 border border-purple-200/50 dark:border-purple-700/50 shadow-lg backdrop-blur-sm"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-500 rounded-xl shadow-lg">
                <SparklesIcon className="h-6 w-6 text-white" />
              </div>
              <div className="text-xs text-purple-600 dark:text-purple-400">
                Son 7 gün
              </div>
            </div>
            <h3 className="text-lg font-semibold text-purple-800 dark:text-purple-200 mb-2">Son Başarım</h3>
            <p className="text-sm text-purple-600 dark:text-purple-400">
              {mockAchievements.find(a => a.completed)?.title || 'Henüz başarım yok'}
            </p>
          </motion.div>
        </div>

        {/* Progress Tracking Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-gray-200/50 dark:border-gray-700/50 shadow-lg"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
              <span>📊</span>
              <span>İlerleme Takibi</span>
            </h2>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Son güncelleme: Bugün
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Category Progress */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">Kategori Bazında İlerleme</h3>
              {categories.filter(cat => cat.id !== 'all').map((category) => {
                 const categoryAchievements = mockAchievements.filter(a => a.category === category.id)
                 const completedCount = categoryAchievements.filter(a => a.completed).length
                 const percentage = categoryAchievements.length > 0 ? (completedCount / categoryAchievements.length) * 100 : 0
                 const Icon = category.icon
                
                return (
                  <motion.div
                    key={category.id}
                    whileHover={{ scale: 1.02 }}
                    className="flex items-center space-x-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200/50 dark:border-gray-600/50"
                  >
                    <div className="p-2 bg-white dark:bg-gray-600 rounded-lg shadow-sm">
                      <Icon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-800 dark:text-gray-200">{category.name}</span>
                        <span className="text-sm font-bold text-gray-600 dark:text-gray-400">
                          {completedCount}/{categoryAchievements.length}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${percentage}%` }}
                          transition={{ duration: 1, delay: 0.5 }}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full"
                        />
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {Math.round(percentage)}% tamamlandı
                      </div>
                    </div>
                  </motion.div>
                )
              })}
            </div>
            
            {/* Rarity Distribution */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">Nadirlık Dağılımı</h3>
              {rarities.filter(r => r.id !== 'all').map((rarity) => {
                const rarityAchievements = mockAchievements.filter(a => a.rarity === rarity.id)
                const completedCount = rarityAchievements.filter(a => a.completed).length
                const percentage = rarityAchievements.length > 0 ? (completedCount / rarityAchievements.length) * 100 : 0
                
                const rarityColors = {
                  common: 'from-gray-400 to-gray-500',
                  rare: 'from-blue-400 to-blue-500',
                  epic: 'from-purple-400 to-purple-500',
                  legendary: 'from-amber-400 to-orange-500'
                }
                
                const rarityEmojis = {
                  common: '⚪',
                  rare: '🔵',
                  epic: '🟣',
                  legendary: '🟡'
                }
                
                return (
                  <motion.div
                    key={rarity.id}
                    whileHover={{ scale: 1.02 }}
                    className="flex items-center space-x-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200/50 dark:border-gray-600/50"
                  >
                    <div className="text-2xl">
                      {rarityEmojis[rarity.id as keyof typeof rarityEmojis]}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-800 dark:text-gray-200">{rarity.name}</span>
                        <span className="text-sm font-bold text-gray-600 dark:text-gray-400">
                          {completedCount}/{rarityAchievements.length}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${percentage}%` }}
                          transition={{ duration: 1, delay: 0.7 }}
                          className={`bg-gradient-to-r ${rarityColors[rarity.id as keyof typeof rarityColors]} h-2 rounded-full`}
                        />
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {Math.round(percentage)}% tamamlandı
                      </div>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Advanced Filters and Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-gray-200/60 dark:border-gray-700/60 mb-8"
      >
        <div className="space-y-6">
          {/* Search and Quick Filters */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-6">
            {/* Enhanced Search */}
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Başarım ara... (başlık, açıklama)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm"
              />
              {searchTerm && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </motion.button>
              )}
            </div>

            {/* Quick Action Buttons */}
            <div className="flex items-center space-x-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowCompleted(!showCompleted)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all duration-200 shadow-sm ${
                  showCompleted
                    ? 'bg-green-500 text-white shadow-green-200 dark:shadow-green-800'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                {showCompleted ? (
                  <CheckCircleIcon className="h-4 w-4" />
                ) : (
                  <XCircleIcon className="h-4 w-4" />
                )}
                <span>Tamamlananlar</span>
              </motion.button>
              
              <div className="relative">
                <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'progress' | 'rarity' | 'date')}
                  className="pl-9 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer shadow-sm"
                >
                  <option value="progress">📊 İlerlemeye Göre</option>
                  <option value="rarity">💎 Nadirliğe Göre</option>
                  <option value="date">📅 Tarihe Göre</option>
                </select>
                <ChevronDownIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Category Filter Pills */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center space-x-2">
              <SparklesIcon className="h-4 w-4" />
              <span>Kategoriler</span>
            </h4>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => {
                const Icon = category.icon
                const isSelected = selectedCategory === category.id
                return (
                  <motion.button
                    key={category.id}
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all duration-200 shadow-sm border ${
                      isSelected
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-blue-200 dark:shadow-blue-800 border-blue-500 transform scale-105'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 border-gray-200 dark:border-gray-600 hover:shadow-md'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{category.name}</span>
                    {isSelected && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="w-2 h-2 bg-white rounded-full"
                      />
                    )}
                  </motion.button>
                )
              })}
            </div>
          </div>

          {/* Rarity Filter */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center space-x-2">
              <StarSolidIcon className="h-4 w-4 text-yellow-500" />
              <span>Nadirlık</span>
            </h4>
            <div className="flex flex-wrap gap-2">
              {rarities.map((rarity) => {
                const isSelected = selectedRarity === rarity.id
                const rarityEmoji = {
                  all: '✨',
                  common: '⚪',
                  rare: '🔵',
                  epic: '🟣',
                  legendary: '🟡'
                }
                return (
                  <motion.button
                    key={rarity.id}
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSelectedRarity(rarity.id)}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg font-medium transition-all duration-200 text-sm border ${
                      isSelected
                        ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-purple-200 dark:shadow-purple-800 border-purple-500'
                        : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 border-gray-200 dark:border-gray-600'
                    }`}
                  >
                    <span>{rarityEmoji[rarity.id as keyof typeof rarityEmoji]}</span>
                    <span>{rarity.name}</span>
                  </motion.button>
                )
              })}
            </div>
          </div>

          {/* Filter Summary */}
          {(selectedCategory !== 'all' || selectedRarity !== 'all' || searchTerm || !showCompleted) && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800"
            >
              <div className="flex items-center space-x-2 text-sm text-blue-700 dark:text-blue-300">
                <FunnelIcon className="h-4 w-4" />
                <span>
                  {filteredAchievements.length} başarım gösteriliyor
                  {searchTerm && ` "${searchTerm}" için`}
                </span>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setSelectedCategory('all')
                  setSelectedRarity('all')
                  setSearchTerm('')
                  setShowCompleted(true)
                }}
                className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 font-medium"
              >
                Filtreleri Temizle
              </motion.button>
            </motion.div>
          )}
        </div>
      </motion.div>

      {/* Leaderboard and Social Features */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8"
      >
        {/* Leaderboard Section */}
        <motion.div
          whileHover={{ scale: 1.02 }}
          className="bg-gradient-to-br from-white/95 via-blue-50/80 to-purple-50/80 dark:from-gray-800/95 dark:via-blue-900/30 dark:to-purple-900/30 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-blue-200/60 dark:border-blue-700/60"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl shadow-lg">
                <TrophySolidIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800 dark:text-white">Liderlik Tablosu</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">En başarılı öğrenciler</p>
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium text-sm"
            >
              Tümünü Gör
            </motion.button>
          </div>
          
          <div className="space-y-3">
            {mockLeaderboard.slice(0, 5).map((user, index) => {
              const isTopThree = index < 3
              const rankColors = {
                0: 'from-yellow-400 to-yellow-600',
                1: 'from-gray-300 to-gray-500',
                2: 'from-amber-600 to-amber-800'
              }
              
              return (
                <motion.div
                  key={user.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.02, x: 4 }}
                  className={`flex items-center space-x-4 p-3 rounded-xl transition-all duration-200 ${
                    isTopThree 
                      ? 'bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 border border-yellow-200/50 dark:border-yellow-700/50'
                      : 'bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    {isTopThree ? (
                      <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${rankColors[index as keyof typeof rankColors]} flex items-center justify-center shadow-lg`}>
                        <span className="text-white font-bold text-sm">{user.rank}</span>
                      </div>
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                        <span className="text-gray-600 dark:text-gray-400 font-semibold text-sm">{user.rank}</span>
                      </div>
                    )}
                    <div className="text-2xl">{user.avatar}</div>
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-gray-800 dark:text-white">{user.name}</h4>
                      <div className="text-right">
                        <div className="text-sm font-bold text-blue-600 dark:text-blue-400">
                          {user.totalXP.toLocaleString()} XP
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Seviye {user.level}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        {user.completedAchievements} başarım
                      </span>
                      {user.badge && (
                        <div className="flex items-center space-x-1">
                          <StarSolidIcon className="h-3 w-3 text-yellow-500" />
                          <span className="text-xs text-yellow-600 dark:text-yellow-400 font-medium">
                            Rozet
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </motion.div>

        {/* Recent Activities Section */}
        <motion.div
          whileHover={{ scale: 1.02 }}
          className="bg-gradient-to-br from-white/95 via-green-50/80 to-emerald-50/80 dark:from-gray-800/95 dark:via-green-900/30 dark:to-emerald-900/30 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-green-200/60 dark:border-green-700/60"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl shadow-lg">
                <BoltIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800 dark:text-white">Son Aktiviteler</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Topluluk başarımları</p>
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 font-medium text-sm"
            >
              Tümünü Gör
            </motion.button>
          </div>
          
          <div className="space-y-3">
            {mockRecentActivities.slice(0, 5).map((activity, index) => {
              const timeAgo = new Date(Date.now() - new Date(activity.completedAt).getTime()).getMinutes()
              const activityIcons = {
                achievement_unlocked: TrophySolidIcon,
                level_up: StarSolidIcon,
                streak_milestone: FireSolidIcon
              }
              const ActivityIcon = activityIcons[activity.type]
              const rarityColor = rarityColors[activity.achievementRarity]
              
              return (
                <motion.div
                  key={activity.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.02, x: -4 }}
                  className="flex items-center space-x-4 p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                >
                  <div className="flex items-center space-x-3">
                    <div className="text-2xl">{activity.userAvatar}</div>
                    <div className={`p-2 rounded-lg ${rarityColor.accent}`}>
                      <ActivityIcon className={`h-4 w-4 ${rarityColor.text}`} />
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-gray-800 dark:text-white text-sm">
                        {activity.userName}
                      </h4>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {timeAgo < 60 ? `${timeAgo}dk önce` : `${Math.floor(timeAgo / 60)}s önce`}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        {activity.type === 'achievement_unlocked' && 'başarım kazandı:'}
                        {activity.type === 'level_up' && 'seviye atladı:'}
                        {activity.type === 'streak_milestone' && 'seri başarısı:'}
                      </span>
                      <span className={`text-xs font-medium ${rarityColor.text}`}>
                        {activity.achievementTitle}
                      </span>
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </motion.div>
      </motion.div>

      {/* Achievements Grid */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        <AnimatePresence>
          {filteredAchievements.map((achievement, index) => {
            const progress = (achievement.current / achievement.target) * 100
            const rarity = rarityColors[achievement.rarity]
            const CategoryIcon = categoryIcons[achievement.type]

            return (
              <motion.div
                key={achievement.id}
                layout
                initial={{ opacity: 0, scale: 0.9, y: 20, rotateX: -15 }}
                animate={{ opacity: 1, scale: 1, y: 0, rotateX: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: -20, rotateX: 15 }}
                transition={{ 
                  delay: index * 0.05,
                  type: "spring",
                  stiffness: 100,
                  damping: 15
                }}
                whileHover={{ 
                  scale: 1.08,
                  y: -12,
                  rotateY: 8,
                  rotateX: 5,
                  z: 100,
                  transition: { type: "spring", stiffness: 300, damping: 20 }
                }}
                whileTap={{ 
                  scale: 0.95,
                  rotateY: 0,
                  rotateX: 0,
                  transition: { duration: 0.1 }
                }}
                onClick={() => setSelectedAchievement(achievement)}
                className={`relative overflow-hidden rounded-2xl border-2 cursor-pointer transition-all duration-500 group perspective-1000 ${
                  rarity.bg
                } ${
                  rarity.border
                } shadow-lg hover:shadow-2xl hover:${rarity.ring} hover:ring-4 backdrop-blur-sm ${
                  achievement.completed ? `hover:${rarity.glow}` : ''
                }`}
                style={{
                  transformStyle: 'preserve-3d'
                }}
              >
                {/* Particle Effects for Completed Achievements */}
                {achievement.completed && (
                  <>
                    <div className={`absolute inset-0 bg-gradient-to-r ${rarity.shimmer} opacity-20 animate-pulse`} />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 animate-shimmer opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                    
                    {/* Floating Particles */}
                    <div className="absolute inset-0 pointer-events-none">
                      {[...Array(6)].map((_, i) => (
                        <motion.div
                          key={i}
                          className="absolute w-1 h-1 bg-white/60 rounded-full"
                          style={{
                            left: `${20 + i * 15}%`,
                            top: `${30 + (i % 3) * 20}%`
                          }}
                          animate={{
                            y: [-10, -30, -10],
                            opacity: [0.3, 1, 0.3],
                            scale: [0.5, 1, 0.5]
                          }}
                          transition={{
                            duration: 2 + i * 0.5,
                            repeat: Infinity,
                            delay: i * 0.3
                          }}
                        />
                      ))}
                    </div>
                    
                    {/* Glow Ring Effect */}
                    <motion.div
                      className="absolute inset-0 rounded-2xl"
                      style={{
                        background: `conic-gradient(from 0deg, ${rarity.accent}, transparent, ${rarity.accent})`,
                        padding: '2px',
                        mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        maskComposite: 'xor'
                      }}
                      animate={{
                        rotate: 360
                      }}
                      transition={{
                        duration: 8,
                        repeat: Infinity,
                        ease: "linear"
                      }}
                    />
                  </>
                )}

                {/* Rarity indicator */}
                <div className={`absolute top-4 right-4 px-2 py-1 rounded-lg text-xs font-bold ${rarity.accent} text-white shadow-md`}>
                  {achievement.rarity.toUpperCase()}
                </div>

                {/* Completion badge */}
                {achievement.completed && (
                  <div className="absolute top-4 left-4 p-1 bg-green-500 rounded-full shadow-lg">
                    <CheckCircleIcon className="h-5 w-5 text-white" />
                  </div>
                )}

                <div className="p-6">
                  {/* Achievement Icon */}
                  <div className="flex items-center justify-center mb-4">
                    <motion.div 
                      whileHover={{ 
                        scale: 1.2, 
                        rotate: [0, -10, 10, 0],
                        transition: { 
                          type: "spring", 
                          stiffness: 400,
                          damping: 10,
                          rotate: {
                            duration: 0.6,
                            repeat: 2
                          }
                        }
                      }}
                      whileTap={{
                        scale: 0.9,
                        rotate: 0
                      }}
                      className={`relative p-4 rounded-2xl ${achievement.completed ? rarity.accent : 'bg-gray-200 dark:bg-gray-700'} shadow-lg transition-all duration-500 group/icon`}
                    >
                      {/* Enhanced Background Pulse */}
                      {achievement.completed && (
                        <>
                          <motion.div
                            className={`absolute inset-0 rounded-2xl ${rarity.accent} opacity-30 blur-lg`}
                            animate={{
                              scale: [1, 1.1, 1],
                              opacity: [0.3, 0.6, 0.3]
                            }}
                            transition={{
                              duration: 2,
                              repeat: Infinity,
                              ease: "easeInOut"
                            }}
                          />
                          <motion.div
                            className="absolute inset-0 rounded-2xl"
                            style={{
                              background: `radial-gradient(circle, ${rarity.accent.includes('amber') ? '#f59e0b' : rarity.accent.includes('purple') ? '#8b5cf6' : rarity.accent.includes('blue') ? '#3b82f6' : '#6b7280'}20, transparent)`
                            }}
                            animate={{
                              scale: [1, 1.3, 1],
                              opacity: [0.4, 0.8, 0.4]
                            }}
                            transition={{
                              duration: 3,
                              repeat: Infinity,
                              ease: "easeInOut"
                            }}
                          />
                        </>
                      )}
                      
                      {/* Main icon with enhanced effects */}
                      <motion.div 
                        className="relative z-10 text-3xl"
                        animate={achievement.completed ? {
                          filter: [
                            'drop-shadow(0 0 8px rgba(59, 130, 246, 0.5))',
                            'drop-shadow(0 0 16px rgba(59, 130, 246, 0.8))',
                            'drop-shadow(0 0 8px rgba(59, 130, 246, 0.5))'
                          ]
                        } : {}}
                        transition={{
                          duration: 2,
                          repeat: Infinity
                        }}
                      >
                        {achievement.icon}
                      </motion.div>
                      
                      {/* Category icon */}
                      <motion.div 
                        className="absolute -bottom-1 -right-1 p-1 bg-white dark:bg-gray-800 rounded-lg shadow-md"
                        whileHover={{ scale: 1.1, rotate: 5 }}
                      >
                        <CategoryIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                      </motion.div>
                      
                      {/* Enhanced Premium Badge for Legendary */}
                      {achievement.rarity === 'legendary' && (
                        <motion.div 
                          className="absolute -top-2 -right-2 text-xl"
                          animate={{
                            y: [0, -5, 0],
                            rotate: [0, 10, -10, 0]
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut"
                          }}
                        >
                          👑
                        </motion.div>
                      )}
                      
                      {/* Enhanced sparkle effect for legendary */}
                      {achievement.completed && achievement.rarity === 'legendary' && (
                        <motion.div 
                          className="absolute -top-1 -right-1"
                          animate={{
                            scale: [1, 1.2, 1],
                            rotate: [0, 180, 360]
                          }}
                          transition={{
                            duration: 3,
                            repeat: Infinity
                          }}
                        >
                          <SparklesSolidIcon className="h-6 w-6 text-yellow-400" />
                        </motion.div>
                      )}
                      
                      {/* Enhanced Sparkle Effect for Epic+ */}
                      {(achievement.rarity === 'epic' || achievement.rarity === 'legendary') && (
                        <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
                          {[...Array(6)].map((_, i) => (
                            <motion.div
                              key={i}
                              className="absolute text-yellow-300"
                              style={{
                                left: `${15 + (i * 15)}%`,
                                top: `${20 + ((i % 3) * 25)}%`,
                                fontSize: i % 2 === 0 ? '12px' : '10px'
                              }}
                              animate={{
                                scale: [0, 1.5, 0],
                                rotate: [0, 360],
                                opacity: [0, 1, 0],
                                x: [0, Math.sin(i) * 15, 0],
                                y: [0, Math.cos(i) * 15, 0]
                              }}
                              transition={{
                                duration: 2.5 + (i * 0.3),
                                repeat: Infinity,
                                delay: i * 0.4,
                                ease: "easeInOut"
                              }}
                            >
                              {i % 3 === 0 ? '✨' : i % 3 === 1 ? '⭐' : '💫'}
                            </motion.div>
                          ))}
                        </div>
                      )}
                      
                      {/* Hover Ripple Effect */}
                      <motion.div
                        className="absolute inset-0 rounded-2xl opacity-0 group-hover/icon:opacity-100"
                        style={{
                          background: `radial-gradient(circle at center, ${achievement.completed ? (rarity.accent.includes('amber') ? '#f59e0b' : rarity.accent.includes('purple') ? '#8b5cf6' : rarity.accent.includes('blue') ? '#3b82f6' : '#6b7280') : '#3b82f6'}40, transparent 70%)`
                        }}
                        initial={{ scale: 0 }}
                        whileHover={{
                          scale: [0, 1.5],
                          opacity: [0, 0.6, 0]
                        }}
                        transition={{
                          duration: 0.8,
                          ease: "easeOut"
                        }}
                      />
                    </motion.div>
                  </div>

                  {/* Achievement Info */}
                  <div className="text-center mb-4">
                    <h3 className={`text-xl font-bold mb-2 tracking-wide drop-shadow-sm group-hover:scale-105 transition-transform duration-300 ${
                      achievement.completed 
                        ? rarity.text + ' drop-shadow-sm' 
                        : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {achievement.title}
                    </h3>
                    
                    <p className={`text-sm leading-relaxed ${
                      achievement.completed 
                        ? 'text-gray-600 dark:text-gray-300' 
                        : 'text-gray-500 dark:text-gray-500'
                    }`}>
                      {achievement.description}
                    </p>
                  </div>

                  {/* Progress Bar */}
                  {!achievement.completed && (
                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          İlerleme
                        </span>
                        <span className="text-sm font-bold text-gray-700 dark:text-gray-300">
                          {achievement.current}/{achievement.target}
                        </span>
                      </div>
                      <div className="relative w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden shadow-inner">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${progress}%` }}
                          transition={{ duration: 1, ease: "easeOut" }}
                          className={`h-full ${rarity.accent} shadow-lg relative overflow-hidden`}
                        >
                          {/* Progress shimmer */}
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse" />
                        </motion.div>
                        {/* Border glow */}
                        <div className={`absolute inset-0 rounded-full border-2 ${rarity.border} opacity-50`} />
                      </div>
                      <div className="text-center mt-2">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                          %{Math.round(progress)} tamamlandı
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Completion Date */}
                  {achievement.completed && achievement.completedAt && (
                    <div className="flex items-center justify-center space-x-2 text-sm text-gray-600 dark:text-gray-400 mb-4">
                      <CalendarIcon className="h-4 w-4" />
                      <span>
                        {new Date(achievement.completedAt).toLocaleDateString('tr-TR')}
                      </span>
                    </div>
                  )}

                  {/* Rewards */}
                  <div className="flex items-center justify-center space-x-4 text-sm">
                    <div className="flex items-center space-x-1">
                      <StarSolidIcon className="h-4 w-4 text-yellow-500" />
                      <span className="font-medium text-gray-700 dark:text-gray-300">
                        {achievement.reward.xp} XP
                      </span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <div className="h-4 w-4 bg-yellow-400 rounded-full shadow-sm" />
                      <span className="font-medium text-gray-700 dark:text-gray-300">
                        {achievement.reward.coins} Coin
                      </span>
                    </div>
                  </div>
                </div>

                {/* Hover effect overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-2xl" />
              </motion.div>
            )
          })}
        </AnimatePresence>
      </motion.div>

      {/* Empty State */}
      {filteredAchievements.length === 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-16"
        >
          <XCircleIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-600 dark:text-gray-400 mb-2">
            Başarım bulunamadı
          </h3>
          <p className="text-gray-500 dark:text-gray-500">
            Arama kriterlerinizi değiştirmeyi deneyin
          </p>
        </motion.div>
      )}

      {/* Achievement Detail Modal */}
      <AnimatePresence>
        {selectedAchievement && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50"
            onClick={() => setSelectedAchievement(null)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              onClick={(e) => e.stopPropagation()}
              className={`relative max-w-md w-full rounded-3xl p-8 shadow-2xl border-2 overflow-hidden backdrop-blur-sm ${
                rarityColors[selectedAchievement.rarity].bg
              } ${
                rarityColors[selectedAchievement.rarity].border
              } ${
                rarityColors[selectedAchievement.rarity].glow
              }`}
            >
              {/* Shimmer effect for completed achievements */}
              {selectedAchievement.completed && (
                <>
                  <div className={`absolute inset-0 bg-gradient-to-r ${rarityColors[selectedAchievement.rarity].shimmer} opacity-20 animate-pulse`} />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 animate-shimmer opacity-0 hover:opacity-100 transition-opacity duration-700" />
                </>
              )}
              {/* Modal content similar to achievement card but with more details */}
              <div className="text-center">
                <div className="mb-6">
                  <div className={`inline-flex p-6 rounded-3xl ${rarityColors[selectedAchievement.rarity].accent} shadow-xl`}>
                    <span className="text-5xl">{selectedAchievement.icon}</span>
                  </div>
                </div>
                
                <h2 className="text-3xl font-bold mb-4 text-gray-900 dark:text-white">
                  {selectedAchievement.title}
                </h2>
                
                <p className="text-lg text-gray-600 dark:text-gray-400 mb-6">
                  {selectedAchievement.description}
                </p>
                
                {/* Detailed progress or completion info */}
                {selectedAchievement.completed ? (
                  <div className="bg-green-100 dark:bg-green-900 p-4 rounded-2xl mb-6">
                    <CheckCircleIcon className="h-8 w-8 text-green-600 dark:text-green-400 mx-auto mb-2" />
                    <p className="text-green-700 dark:text-green-300 font-semibold">
                      Başarım Tamamlandı!
                    </p>
                    <p className="text-sm text-green-600 dark:text-green-400">
                      {new Date(selectedAchievement.completedAt!).toLocaleDateString('tr-TR')}
                    </p>
                  </div>
                ) : (
                  <div className="mb-6">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        İlerleme
                      </span>
                      <span className="text-sm font-bold text-gray-700 dark:text-gray-300">
                        {selectedAchievement.current}/{selectedAchievement.target}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 overflow-hidden">
                      <div
                        className={`h-full ${rarityColors[selectedAchievement.rarity].accent} transition-all duration-500`}
                        style={{ width: `${(selectedAchievement.current / selectedAchievement.target) * 100}%` }}
                      />
                    </div>
                  </div>
                )}
                
                {/* Rewards */}
                <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-2xl mb-6">
                  <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3">Ödüller</h4>
                  <div className="flex justify-center space-x-6">
                    <div className="text-center">
                      <StarSolidIcon className="h-6 w-6 text-yellow-500 mx-auto mb-1" />
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {selectedAchievement.reward.xp} XP
                      </p>
                    </div>
                    <div className="text-center">
                      <div className="h-6 w-6 bg-yellow-400 rounded-full mx-auto mb-1" />
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {selectedAchievement.reward.coins} Coin
                      </p>
                    </div>
                    {selectedAchievement.reward.badge && (
                      <div className="text-center">
                        <TrophySolidIcon className="h-6 w-6 text-purple-500 mx-auto mb-1" />
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Rozet
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Close Button */}
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setSelectedAchievement(null)}
                  className={`absolute top-6 right-6 p-3 rounded-full transition-all duration-300 z-10 ${
                    selectedAchievement.completed 
                      ? `${rarityColors[selectedAchievement.rarity].accent} ${rarityColors[selectedAchievement.rarity].text} shadow-lg hover:shadow-xl border-2 ${rarityColors[selectedAchievement.rarity].border}`
                      : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-400 border-2 border-gray-300 dark:border-gray-600'
                  }`}
                >
                  {selectedAchievement.rarity === 'legendary' && selectedAchievement.completed ? (
                    <span className="text-lg">✨</span>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                </motion.button>
                
                {/* Main Close Button */}
                 <motion.button
                   whileHover={{ scale: 1.05 }}
                   whileTap={{ scale: 0.95 }}
                   onClick={() => setSelectedAchievement(null)}
                   className={`w-full mt-6 py-3 px-6 rounded-xl font-semibold transition-all duration-300 ${
                     selectedAchievement.completed
                       ? `${rarityColors[selectedAchievement.rarity].accent} ${rarityColors[selectedAchievement.rarity].text} hover:shadow-lg border-2 ${rarityColors[selectedAchievement.rarity].border}`
                       : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 border-2 border-gray-300 dark:border-gray-600'
                   }`}
                 >
                   Kapat
                 </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default Achievements